import { supabase } from '@/integrations/supabase/client';

// Cache for cities loaded from DB (with translations)
let citiesCache: Array<{
  id: string;
  label: string;
  label_en?: string;
  label_fr?: string;
  label_ru?: string;
}> = [];
let citiesLoaded = false;

/**
 * Load cities dynamically from israeli_settlements table
 * Uses range() to fetch all 1270+ settlements (Supabase default limit is 1000)
 * Includes translation columns for multi-language support
 */
export async function loadCitiesFromDB(): Promise<void> {
  if (citiesLoaded) return;

  try {
    // First batch: 0-999
    const { data: data1, error: error1 } = await supabase
      .from('israeli_settlements')
      .select('settlement_name, settlement_name_en, settlement_name_fr, settlement_name_ru')
      .order('settlement_name')
      .range(0, 999);

    // Second batch: 1000+
    const { data: data2, error: error2 } = await supabase
      .from('israeli_settlements')
      .select('settlement_name, settlement_name_en, settlement_name_fr, settlement_name_ru')
      .order('settlement_name')
      .range(1000, 1999);

    if (error1 || error2) {
      console.error('Error loading cities from DB:', error1 || error2);
      return;
    }

    const allData = [...(data1 || []), ...(data2 || [])];

    citiesCache = [
      { id: 'all', label: 'כל הארץ', label_en: 'All Israel', label_fr: 'Tout Israël', label_ru: 'Вся страна' },
      ...allData.map(s => ({
        id: s.settlement_name,
        label: s.settlement_name,
        label_en: s.settlement_name_en,
        label_fr: s.settlement_name_fr,
        label_ru: s.settlement_name_ru
      }))
    ];
    citiesLoaded = true;

    console.log(`Loaded ${citiesCache.length - 1} cities from DB`);
  } catch (error) {
    console.error('Error in loadCitiesFromDB:', error);
  }
}

/**
 * Get cities - returns cached cities or fallback to static list
 * Includes translation columns for multi-language support
 */
export function getCities(): Array<{
  id: string;
  label: string;
  label_en?: string;
  label_fr?: string;
  label_ru?: string;
}> {
  return citiesCache.length > 0 ? citiesCache : israelLocations;
}

/**
 * Check if cities are loaded from DB
 */
export function isCitiesLoaded(): boolean {
  return citiesLoaded;
}

// Static fallback list of cities and towns in Israel
export const israelLocations = [
  { id: 'all', label: 'כל הארץ' },
  { id: 'tel_aviv', label: 'תל אביב' },
  { id: 'jerusalem', label: 'ירושלים' },
  { id: 'haifa', label: 'חיפה' },
  { id: 'rishon', label: 'ראשון לציון' },
  { id: 'petah_tikva', label: 'פתח תקווה' },
  { id: 'ashdod', label: 'אשדוד' },
  { id: 'netanya', label: 'נתניה' },
  { id: 'beer_sheva', label: 'באר שבע' },
  { id: 'bnei_brak', label: 'בני ברק' },
  { id: 'holon', label: 'חולון' },
  { id: 'ramat_gan', label: 'רמת גן' },
  { id: 'rehovot', label: 'רחובות' },
  { id: 'herzliya', label: 'הרצליה' },
  { id: 'bat_yam', label: 'בת ים' },
  { id: 'kfar_saba', label: 'כפר סבא' },
  { id: 'ashkelon', label: 'אשקלון' },
  { id: 'modiin', label: 'מודיעין-מכבים-רעות' },
  { id: 'raanana', label: 'רעננה' },
  { id: 'nahariya', label: 'נהריה' },
  { id: 'givataim', label: 'גבעתיים' },
  { id: 'hadera', label: 'חדרה' },
  { id: 'lod', label: 'לוד' },
  { id: 'ramla', label: 'רמלה' },
  { id: 'ramat_hasharon', label: 'רמת השרון' },
  { id: 'hod_hasharon', label: 'הוד השרון' },
  { id: 'karmiel', label: 'כרמיאל' },
  { id: 'tiberias', label: 'טבריה' },
  { id: 'eilat', label: 'אילת' },
  { id: 'akko', label: 'עכו' },
  { id: 'afula', label: 'עפולה' },
  { id: 'dimona', label: 'דימונה' },
  { id: 'nazareth', label: 'נצרת' },
  { id: 'yavne', label: 'יבנה' },
  { id: 'kiryat_gat', label: 'קרית גת' },
  { id: 'kiryat_motzkin', label: 'קרית מוצקין' },
  { id: 'kiryat_yam', label: 'קרית ים' },
  { id: 'kiryat_bialik', label: 'קרית ביאליק' },
  { id: 'kiryat_ono', label: 'קרית אונו' },
  { id: 'kiryat_shmona', label: 'קרית שמונה' },
  { id: 'kiryat_malachi', label: 'קרית מלאכי' },
  { id: 'sderot', label: 'שדרות' },
  { id: 'ness_ziona', label: 'נס ציונה' },
  { id: 'maalot_tarshiha', label: 'מעלות-תרשיחא' },
  { id: 'migdal_haemek', label: 'מגדל העמק' },
  { id: 'yokneam', label: 'יקנעם' },
  { id: 'or_akiva', label: 'אור עקיבא' },
  { id: 'beit_shemesh', label: 'בית שמש' },
  { id: 'tzfat', label: 'צפת' },
  { id: 'ofakim', label: 'אופקים' },
  { id: 'netivot', label: 'נתיבות' },
  { id: 'arad', label: 'ערד' },
  { id: 'rosh_haayin', label: 'ראש העין' },
  { id: 'tira', label: 'טירה' },
  { id: 'tirat_carmel', label: 'טירת כרמל' },
  { id: 'modiin_illit', label: 'מודיעין עילית' },
  { id: 'beitar_illit', label: 'ביתר עילית' },
  { id: 'katzrin', label: 'קצרין' },
  { id: 'shoham', label: 'שוהם' },
  { id: 'even_yehuda', label: 'אבן יהודה' },
  { id: 'kiryat_arba', label: 'קרית ארבע' },
  { id: 'givat_shmuel', label: 'גבעת שמואל' },
  { id: 'gedera', label: 'גדרה' }
];

// Various regions of Israel
export const israelRegions = [
  { id: 'all', label: 'כל הארץ' },
  { id: 'tel_aviv', label: 'תל אביב והמרכז' },
  { id: 'jerusalem', label: 'ירושלים והסביבה' },
  { id: 'haifa', label: 'חיפה והצפון' },
  { id: 'beer_sheva', label: 'באר שבע והדרום' },
  { id: 'sharon', label: 'השרון' },
  { id: 'shfela', label: 'השפלה' },
  { id: 'eilat', label: 'אילת והערבה' },
  { id: 'galilee', label: 'הגליל' },
  { id: 'golan', label: 'רמת הגולן' },
  { id: 'negev', label: 'הנגב' }
];
